import { match } from 'ts-pattern'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { AutomationExecutionStatus, LegacyWorkflow } from '../types'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

type WorkflowStatus = Dashboardv99990101Api.Watchtower.WorkflowStatus
type WorkflowTask = Dashboardv99990101Api.Watchtower.WorkflowTask
type AutomationExecution = Dashboardv99990101Api.Automations.AutomationExecution
type AutomationExecutionTask =
  Dashboardv99990101Api.Automations.AutomationExecutionTask
type AutomationExecutionTaskStatus =
  Dashboardv99990101Api.Automations.AutomationExecutionTaskStatus

export const mapLegacyStatusToExecutionStatus = (
  status: WorkflowStatus
): AutomationExecutionStatus =>
  match(status)
    .with('IN_PROGRESS', 'NEEDS_REVIEW', () => 'RUNNING' as const)
    .with('RESOLVED', () => 'COMPLETED' as const)
    .with('FAILED', () => 'FAILED' as const)
    .with('CANCELLED', () => 'TERMINATED' as const)
    .exhaustive()

export const mapExecutionStatusToLegacyStatuses = (
  status: string
): WorkflowStatus[] =>
  match(status)
    .with('RUNNING', () => ['IN_PROGRESS', 'NEEDS_REVIEW'] as WorkflowStatus[])
    .with('COMPLETED', () => ['RESOLVED'] as WorkflowStatus[])
    .with('FAILED', () => ['FAILED'] as WorkflowStatus[])
    .with('TERMINATED', () => ['CANCELLED'] as WorkflowStatus[])
    .otherwise(() => [])

const mapLegacyTaskStatus = (
  status: WorkflowTask['status']
): AutomationExecutionTaskStatus =>
  match(status)
    .with('COMPLETED', () => 'COMPLETED' as const)
    .with('FAILED', () => 'FAILED' as const)
    .with('PENDING', () => 'IN_PROGRESS' as const)
    .exhaustive()

const taskTypeConfig: Record<
  WorkflowTask['type'],
  { nodeType: string; executor: string }
> = {
  CONTRACT_EXTRACTION: {
    nodeType: 'ACTION_CONTRACT_EXTRACTION',
    executor: TASK_EXECUTORS.extractContract
  },
  CONTRACT_REVIEW: {
    nodeType: 'REVIEW_CONTRACT',
    executor: TASK_EXECUTORS.reviewContract
  },
  SCHEDULE_CREATION: {
    nodeType: 'ACTION_CREATE_SCHEDULE',
    executor: TASK_EXECUTORS.createSchedule
  }
}

const adaptContractExtractionData = (
  task: WorkflowTask
): {
  inputData: Record<string, unknown>
  outputData: Record<string, unknown>
} => ({
  inputData: task.input ?? {},
  outputData: task.output?.data ? { extractedData: task.output.data } : {}
})

const adaptContractReviewData = (
  task: WorkflowTask
): {
  inputData: Record<string, unknown>
  outputData: Record<string, unknown>
} => {
  const inputData =
    (task.input?.data as Record<string, unknown>) ?? task.input ?? {}

  return {
    inputData: {
      assignmentRules: inputData.assignmentRules ?? { users: [] },
      billingSchedule: inputData.billingSchedule,
      customer: inputData.customer
    },
    outputData: {
      ...(task.output ?? {}),
      completedBy: task.output?.completedBy
    }
  }
}

const adaptScheduleCreationData = (
  task: WorkflowTask
): {
  inputData: Record<string, unknown>
  outputData: Record<string, unknown>
} => ({
  inputData: task.input ?? {},
  outputData: {
    ...(task.output ?? {}),
    billingScheduleId: task.output?.scheduleId
  }
})

const taskDataAdapters: Record<
  WorkflowTask['type'],
  (task: WorkflowTask) => {
    inputData: Record<string, unknown>
    outputData: Record<string, unknown>
  }
> = {
  CONTRACT_EXTRACTION: adaptContractExtractionData,
  CONTRACT_REVIEW: adaptContractReviewData,
  SCHEDULE_CREATION: adaptScheduleCreationData
}

const adaptLegacyTask = (task: WorkflowTask): AutomationExecutionTask => {
  const config = taskTypeConfig[task.type]
  const nodeId = `legacy-${task.id}`
  const { inputData, outputData } = taskDataAdapters[task.type](task)

  return {
    taskId: task.id,
    status: mapLegacyTaskStatus(task.status),
    taskReferenceName: `${nodeId}:${config.nodeType}:${config.executor}`,
    taskType: 'SIMPLE',
    startTime: task.createdAt,
    inputData: {
      taskExecutorConfiguration: {
        executor: config.executor,
        version: '1'
      },
      data: inputData
    },
    outputData
  }
}

export const adaptLegacyWorkflowToExecution = (
  workflow: LegacyWorkflow
): AutomationExecution => {
  const extractionTask = workflow.tasks.find(
    t => t.type === 'CONTRACT_EXTRACTION'
  )
  const contractName = (
    extractionTask?.output?.data as { contract?: { name?: string } } | undefined
  )?.contract?.name
  const fileName = extractionTask?.input?.assetFileName ?? contractName

  return {
    id: `legacy-${workflow.id}`,
    automationId: `legacy-${workflow.id}`,
    automationVersionId: `legacy-${workflow.id}`,
    status: mapLegacyStatusToExecutionStatus(workflow.status),
    input: {
      triggerType: 'CONTRACT_UPLOADED',
      fileName
    },
    output: {},
    variables: {},
    tasks: workflow.tasks.map(adaptLegacyTask),
    createTime: workflow.createdAt,
    updateTime: workflow.updatedAt,
    failedReferenceTaskNames: workflow.tasks
      .filter(t => t.status === 'FAILED')
      .map(t => {
        const config = taskTypeConfig[t.type]
        return `legacy-${t.id}:${config.nodeType}:${config.executor}`
      })
  }
}
